import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetLogisticOperatorsQuery,
  useGetLogisticOperatorByIdQuery,
} from '../services/logistic-operators.service';
import type { LogisticOperator } from '../models';
import type { LogisticOperatorsFilter } from '../models';
import { resolveLogisticOperatorsListQuery } from '../utils/resolveLogisticOperatorsListQuery';

interface UseLogisticOperatorsProps {
  filters?: LogisticOperatorsFilter;
  page: number;
  pageSize: number;
  skip?: boolean;
}

interface UseLogisticOperatorsResult {
  isLoading: boolean;
  error: unknown;
  logisticOperators: LogisticOperator[];
  totalItems: number;
  totalPages: number;
  isContradictoryFilter: boolean;
}

const useLogisticOperators = ({
  filters,
  page,
  pageSize,
  skip: skipProp,
}: UseLogisticOperatorsProps): UseLogisticOperatorsResult => {
  const resolved = resolveLogisticOperatorsListQuery(filters);

  const isContradictoryFilter = resolved.kind === 'contradictory';

  const listQueryArgs =
    resolved.kind === 'paginatedList' && !isContradictoryFilter
      ? {
          paginated: { page, pageSize },
          search: resolved.name,
          businessName: resolved.businessName,
          status: resolved.status,
        }
      : skipToken;

  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
    error: listError,
  } = useGetLogisticOperatorsQuery(listQueryArgs, {
    skip: skipProp === true || resolved.kind !== 'paginatedList',
  });

  const byIdArg =
    resolved.kind === 'byId' && !skipProp
      ? { logisticOperatorId: resolved.logisticOperatorId }
      : skipToken;

  const {
    data: singleOperator,
    isLoading: isByIdLoading,
    isFetching: isByIdFetching,
    error: byIdError,
  } = useGetLogisticOperatorByIdQuery(byIdArg, {
    skip: skipProp === true || resolved.kind !== 'byId',
  });

  if (isContradictoryFilter) {
    return {
      isLoading: false,
      error: undefined,
      logisticOperators: [],
      totalItems: 0,
      totalPages: 0,
      isContradictoryFilter: true,
    };
  }

  if (resolved.kind === 'byId') {
    const rows = singleOperator ? [singleOperator] : [];
    return {
      isLoading: isByIdLoading || isByIdFetching,
      error: byIdError,
      logisticOperators: rows,
      totalItems: rows.length,
      totalPages: 1,
      isContradictoryFilter: false,
    };
  }

  const count = listData?.count ?? 0;
  const rows = listData?.items ?? [];

  return {
    isLoading: isListLoading || isListFetching,
    error: listError,
    logisticOperators: rows,
    totalItems: count,
    totalPages: Math.ceil(count / pageSize),
    isContradictoryFilter: false,
  };
};

export default useLogisticOperators;
